import { useState } from 'react'
import { Outlet, useMatch, useParams, useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { F0Box } from '@factorialco/f0-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { Box } from '@factorialco/deprecated-design-system'
import {
  BudgetFragment,
  EmployeesTeamsQuery,
  FinanceBudgetDetailRevampPageDocument,
  GetBudgetDocument,
} from 'generated/resources/graphql'
import { compact, uniq } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useTeams } from 'modules/employees/hooks/useTeams'
import AddEmployeeModal from 'modules/finance/components/Budgets/Detail/AddEmployeeModal'
import EditBudgetSidepanelLegacy from 'modules/finance/components/Budgets/Detail/EditBudgetSidePanel'
import PeriodNavigation from 'modules/finance/components/Budgets/Detail/PeriodNavigation'
import PeriodStatusCard from 'modules/finance/components/Budgets/Detail/PeriodStatusCard'
import BudgetTabs from 'modules/finance/components/Budgets/Detail/Tabs'
import { BudgetAmountWidgets } from 'modules/finance/components/Budgets/BudgetDetailDataCollection/AmountWidgets'
import { EditBudgetSidepanel } from 'modules/finance/components/Budgets/ExpenseBudgetDetailDataCollection/EditBudgetSidepanel'
import { ExpenseBudgetDetailContext } from 'modules/finance/pages/ExpenseBudgetDetailPage'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type TeamsTeam = EmployeesTeamsQuery['teams']['teams'][number]

const BudgetDetailsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    access: { locale },
  } = useCurrentSession()

  const toggleEmployeeModal = () => setSearchParams({ modal: 'employee' })

  const { budgetId } = useParams()

  const { teams, loading: loadingTeams } = useTeams()
  const { has_dev_training_budgets } = useFeatures(['dev_training_budgets'])

  const [budget, setBudget] = useState<BudgetFragment>()
  const [periodStart, setPeriodStart] = useState<string>()
  const [periodEnd, setPeriodEnd] = useState<string>()

  // Check if we're on a child route (records or employees tabs)
  const recordsMatch = useMatch(
    navigation.finance.spending.expenses.budgets.detail.records.fullPath({
      budgetId: budgetId ?? '',
    })
  )
  const employeesMatch = useMatch(
    navigation.finance.spending.expenses.budgets.detail.employees.fullPath({
      budgetId: budgetId ?? '',
    })
  )
  const isOnChildRoute = !!(recordsMatch || employeesMatch)

  const {
    data,
    loading: loadingBudget,
    error,
    refetch: refetchBudget,
  } = useQuery(GetBudgetDocument, {
    variables: {
      id: budgetId ?? '',
      ...(periodStart && { periodStart: periodStart }),
    },
    fetchPolicy: 'cache-first',
  })

  // Fetch data for expense budget detail (new UI with tabs)
  const {
    data: expenseBudgetData,
    loading: loadingExpenseBudget,
    error: expenseBudgetError,
    refetch: refetchExpenseBudget,
  } = useQuery(FinanceBudgetDetailRevampPageDocument, {
    variables: { budgetId: budgetId ?? '' },
    skip: !has_dev_training_budgets || !isOnChildRoute,
  })

  const budgets = data?.finance?.budgetsConnection?.nodes || []
  const movements = data?.finance?.budgetMovementsConnection?.nodes || []
  const categories = data?.expenses?.categoriesConnection?.nodes || []
  const allocations = data?.finance?.budgetAllocationsConnection?.nodes || []
  const canSeeBudgets = data?.finance?._policies?.budgetsSeeBudgets?.can

  const employeeIdsWithBudget = compact(
    uniq(allocations.map((allocation) => allocation.employee?.id))
  )

  const closeModal = () => {
    setSearchParams({})
  }

  const openModal = () => {
    toggleEmployeeModal()
  }

  const handleSubmit = () => {
    refetchBudget({ id: budget?.id, periodStart: budget?.currentPeriodStart })
    setSearchParams({})
  }

  const budgetName = budgets[0]?.name
  const budgetDescription = budgets[0]?.description
  const budgetAmountCents = budgets[0]?.amountCents
  const budgetArchived = budgets[0]?.archived
  const budgetEffectiveFrom = budgets[0]?.effectiveFrom
  const budgetEffectiveUntil = budgets[0]?.effectiveUntil
  const budgetTotalAllocatedAmountCents = budgets[0]?.totalAllocatedAmountCents

  useEffect(() => {
    if (!loadingBudget) {
      setBudget(budgets[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadingBudget,
    budgetName,
    budgetDescription,
    budgetAmountCents,
    budgetArchived,
    budgetEffectiveFrom,
    budgetEffectiveUntil,
    budgetTotalAllocatedAmountCents,
  ])

  const loading = !budget && (loadingBudget || loadingTeams)

  const isExpenseBudget = budget?.budgetableBy === 'expensable' && budgetId
  const expenseBudgetRecordsPath = navigation.expenses.budgets.detail.records.fullPath({
    budgetId: budgetId ?? '',
  })

  // Redirect to expense budget records tab when FF enabled (only from base path)
  useEffect(() => {
    if (isExpenseBudget && has_dev_training_budgets && !isOnChildRoute) {
      navigate(expenseBudgetRecordsPath, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpenseBudget, has_dev_training_budgets, expenseBudgetRecordsPath, isOnChildRoute])

  // New expense budget UI with tabs (when FF enabled and on child route)
  if (has_dev_training_budgets && isExpenseBudget && isOnChildRoute) {
    const expenseBudget = expenseBudgetData?.finance?.budget
    const amounts = expenseBudgetData?.finance?.budgetAmountsConnection?.nodes[0]

    const handleEditClose = () => {
      setSearchParams({})
    }

    const handleEditSuccess = async () => {
      await refetchExpenseBudget()
      setSearchParams({})
    }

    const context: ExpenseBudgetDetailContext | null =
      expenseBudget && amounts
        ? { budget: expenseBudget, amounts, locale, refetch: refetchExpenseBudget }
        : null

    return (
      <ResponsivePage.Body loading={loadingExpenseBudget} error={expenseBudgetError}>
        {context && (
          <F0Box display="flex" flexDirection="column" gap="xl">
            <BudgetAmountWidgets budget={context.budget} initialAmounts={context.amounts} />
            <Outlet context={context} />
          </F0Box>
        )}
        {searchParams.get('modal') === 'edit' && expenseBudget && (
          <EditBudgetSidepanel
            budget={expenseBudget}
            onClose={handleEditClose}
            onSuccess={handleEditSuccess}
          />
        )}
      </ResponsivePage.Body>
    )
  }

  // Old budget details UI (legacy)
  return (
    <ResponsivePage.Body
      variant="wide"
      showHeader={false}
      authorized={canSeeBudgets}
      loading={loading}
      error={error}
    >
      {budget && (
        <Box gap="s32">
          {budget.started && (
            <PeriodNavigation
              budget={budget}
              onPeriodChange={(start, end) => {
                setPeriodStart(start)
                setPeriodEnd(end)
              }}
            />
          )}
          <PeriodStatusCard budget={budget} />
          <BudgetTabs
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            teams={teams as TeamsTeam[]}
            budget={budget}
            currency={budget.currency}
            movements={movements}
            categories={categories}
            periodStart={periodStart}
            periodEnd={periodEnd}
            openEmployeeModal={openModal}
            allocations={allocations}
            refetchBudget={refetchBudget}
          />
          {searchParams.get('modal') === 'edit' && (
            <EditBudgetSidepanelLegacy
              onClose={() => setSearchParams({})}
              openEditBudgetSidePanel={searchParams.get('modal') === 'edit'}
              budget={budget}
              onSubmit={handleSubmit}
            />
          )}
          {searchParams.get('modal') === 'employee' && (
            <AddEmployeeModal
              budget={budget}
              employeeIdsWithBudget={employeeIdsWithBudget}
              closeModal={closeModal}
              refetchBudget={refetchBudget}
            />
          )}
        </Box>
      )}
    </ResponsivePage.Body>
  )
}

export default BudgetDetailsPage
